import { useState, useRef, useCallback, useEffect } from 'react'
import {
  Upload,
  X,
  FileText,
  Loader2,
  ChevronRight,
  Sparkles,
  FlaskConical,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react'

// ─── Types ───────────────────────────────────────────────────────────────────

interface LabAnchor {
  x: number
  y: number
}

interface LabQuestion {
  question_number: string
  score: number
  maximum_points: number
  reasoning: string
  feedback: string
  student_answer: string
  page_index: number
  label_anchor: LabAnchor
}

interface LabMeta {
  provider: string
  model: string
  usage?: Record<string, unknown>
  page_locator_enabled?: boolean
}

interface LabResult {
  page_file_ids: string[]
  results: LabQuestion[]
  total_score: number
  overall_feedback: string
  meta: LabMeta
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function scoreColor(score: number, max: number): 'green' | 'orange' | 'red' {
  if (max === 0) return 'green'
  const ratio = score / max
  if (ratio >= 0.8) return 'green'
  if (ratio >= 0.4) return 'orange'
  return 'red'
}

function ScoreBadge({ score, max, size = 'md' }: { score: number; max: number; size?: 'sm' | 'md' | 'lg' }) {
  const color = scoreColor(score, max)
  const cls = {
    green:  'lab-badge-green',
    orange: 'lab-badge-orange',
    red:    'lab-badge-red',
  }[color]
  const sizeCls = { sm: 'lab-badge-sm', md: 'lab-badge-md', lg: 'lab-badge-lg' }[size]
  return (
    <span className={`lab-badge ${cls} ${sizeCls}`}>
      {score}/{max}
    </span>
  )
}

// ─── File drop zone ───────────────────────────────────────────────────────────

interface DropZoneProps {
  label: string
  hint?: string
  accept: string
  multiple?: boolean
  files: File[]
  onFiles: (files: File[]) => void
}

function DropZone({ label, hint, accept, multiple = false, files, onFiles }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const dropped = Array.from(e.dataTransfer.files)
    onFiles(multiple ? dropped : [dropped[0]])
  }, [multiple, onFiles])

  const removeFile = (index: number) => {
    const next = files.filter((_, i) => i !== index)
    onFiles(next)
  }

  return (
    <div className="lab-dropzone-wrap">
      <label className="lab-field-label">{label}</label>
      {hint && <p className="lab-field-hint">{hint}</p>}

      {files.length === 0 ? (
        <div
          className={`lab-dropzone ${dragging ? 'lab-dropzone--drag' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <Upload size={20} className="lab-dropzone-icon" />
          <span className="lab-dropzone-cta">
            Sürükleyin veya <span className="lab-link">seçin</span>
          </span>
          <span className="lab-dropzone-types">{accept}</span>
        </div>
      ) : (
        <div className="lab-file-list">
          {files.map((f, i) => (
            <div key={i} className="lab-file-item">
              <FileText size={14} />
              <span className="lab-file-name">{f.name}</span>
              <button
                className="lab-file-remove"
                onClick={() => removeFile(i)}
                type="button"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {multiple && (
            <button
              className="lab-add-more"
              onClick={() => inputRef.current?.click()}
              type="button"
            >
              + Sayfa ekle
            </button>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={e => {
          const picked = Array.from(e.target.files || [])
          onFiles(multiple ? [...files, ...picked] : [picked[0]])
          e.target.value = ''
        }}
      />
    </div>
  )
}

// ─── Level-1 tab: side-by-side ────────────────────────────────────────────────

function Level1Tab({
  result,
  selected,
  onSelect,
}: {
  result: LabResult
  selected: string | null
  onSelect: (qnum: string | null) => void
}) {
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [flashPage, setFlashPage] = useState<number | null>(null)

  // Group questions by page for badges
  const byPage: Record<number, LabQuestion[]> = {}
  result.results.forEach(q => {
    ;(byPage[q.page_index] ??= []).push(q)
  })

  const scrollToPage = (pageIndex: number) => {
    const el = pageRefs.current[pageIndex]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setFlashPage(pageIndex)
    setTimeout(() => setFlashPage(null), 800)
  }

  const handleCardClick = (q: LabQuestion) => {
    const next = selected === q.question_number ? null : q.question_number
    onSelect(next)
    if (next) scrollToPage(q.page_index)
  }

  return (
    <div className="lab-split">
      {/* LEFT — page images */}
      <div className="lab-split-left">
        <div className="lab-page-rail">
          {result.page_file_ids.map((fid, i) => (
            <div
              key={fid}
              ref={el => { pageRefs.current[i] = el }}
              className={`lab-page-card ${flashPage === i ? 'lab-page-card--flash' : ''}`}
            >
              <div className="lab-page-label">
                <span>Sayfa {i + 1}</span>
                {(byPage[i] || []).length > 0 && (
                  <span className="lab-page-qbadges">
                    {(byPage[i] || []).map(q => (
                      <span
                        key={q.question_number}
                        className={`lab-page-qbadge lab-page-qbadge--${scoreColor(q.score, q.maximum_points)}`}
                      >
                        S{q.question_number}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              <img
                src={`/stream_file/${fid}`}
                alt={`Sayfa ${i + 1}`}
                className="lab-page-img"
              />
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT — report */}
      <div className="lab-split-right">
        <ReportPanel
          result={result}
          selected={selected}
          onSelect={handleCardClick}
          showPageBadge
        />
      </div>
    </div>
  )
}

// ─── Level-2 tab: pins anchored at the PRINTED question label ────────────────

function LabelPin({
  q,
  isSelected,
  onClick,
}: {
  q: LabQuestion
  isSelected: boolean
  onClick: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const color = scoreColor(q.score, q.maximum_points)
  const colorCls = { green: 'lab-label-pin-green', orange: 'lab-label-pin-orange', red: 'lab-label-pin-red' }[color]

  // The anchor marks the top-left of the printed label. The badge sits
  // slightly above-right so the printed label itself stays readable.
  const style: React.CSSProperties = {
    left: `clamp(2px, ${q.label_anchor.x * 100}%, calc(100% - 96px))`,
    top:  `clamp(2px, ${q.label_anchor.y * 100}%, calc(100% - 30px))`,
  }

  return (
    <button
      type="button"
      aria-label={`Soru ${q.question_number}: ${q.score}/${q.maximum_points} puan`}
      className={`lab-label-pin ${colorCls} ${isSelected ? 'lab-label-pin--selected' : ''}`}
      style={style}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="lab-label-pin-dot" aria-hidden />
      <span className="lab-label-pin-text">
        S{q.question_number} · {q.score}/{q.maximum_points}
      </span>

      {hovered && (
        <div className="lab-label-pin-popover">
          <div className="lab-label-pin-popover-qnum">S{q.question_number}</div>
          <div className="lab-label-pin-popover-feedback">{q.feedback}</div>
          {q.student_answer && (
            <div className="lab-label-pin-popover-answer">
              <span className="lab-label-pin-popover-label">Cevap:</span> {q.student_answer}
            </div>
          )}
        </div>
      )}
    </button>
  )
}

function Level2Tab({
  result,
  selected,
  onSelect,
}: {
  result: LabResult
  selected: string | null
  onSelect: (qnum: string | null) => void
}) {
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [flashPage, setFlashPage] = useState<number | null>(null)

  // When a question is selected externally, scroll to its page
  useEffect(() => {
    if (!selected) return
    const q = result.results.find(r => r.question_number === selected)
    if (q) {
      const el = pageRefs.current[q.page_index]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setFlashPage(q.page_index)
        setTimeout(() => setFlashPage(null), 800)
      }
    }
  }, [selected, result.results])

  const handlePinClick = (q: LabQuestion) => {
    onSelect(selected === q.question_number ? null : q.question_number)
  }

  // Group questions by page for efficient rendering + badges
  const byPage: Record<number, LabQuestion[]> = {}
  result.results.forEach(q => {
    ;(byPage[q.page_index] ??= []).push(q)
  })

  return (
    <div className="lab-split">
      {/* LEFT — pages with pins anchored at printed question labels */}
      <div className="lab-split-left">
        <div className="lab-page-rail">
          {result.page_file_ids.map((fid, i) => {
            const pageQuestions = byPage[i] || []
            return (
              <div
                key={fid}
                ref={el => { pageRefs.current[i] = el }}
                className={`lab-page-card ${flashPage === i ? 'lab-page-card--flash' : ''}`}
              >
                <div className="lab-page-label">
                  <span>Sayfa {i + 1}</span>
                  {pageQuestions.length > 0 && (
                    <span className="lab-page-qbadges">
                      {pageQuestions.map(q => (
                        <span
                          key={q.question_number}
                          className={`lab-page-qbadge lab-page-qbadge--${scoreColor(q.score, q.maximum_points)}`}
                        >
                          S{q.question_number}
                        </span>
                      ))}
                    </span>
                  )}
                </div>

                <div className="lab-page-img-wrap">
                  <img
                    src={`/stream_file/${fid}`}
                    alt={`Sayfa ${i + 1}`}
                    className="lab-page-img"
                    draggable={false}
                  />
                  {pageQuestions.map(q => (
                    <LabelPin
                      key={q.question_number}
                      q={q}
                      isSelected={selected === q.question_number}
                      onClick={() => handlePinClick(q)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* RIGHT — report */}
      <div className="lab-split-right">
        <ReportPanel
          result={result}
          selected={selected}
          onSelect={q => onSelect(selected === q.question_number ? null : q.question_number)}
          showPageBadge
        />
      </div>
    </div>
  )
}

// ─── Shared report panel ──────────────────────────────────────────────────────

function ReportPanel({
  result,
  selected,
  onSelect,
  showPageBadge,
}: {
  result: LabResult
  selected: string | null
  onSelect: (q: LabQuestion) => void
  showPageBadge: boolean
}) {
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Scroll to card when selected changes
  useEffect(() => {
    if (!selected) return
    const el = cardRefs.current[selected]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selected])

  const totalPercent =
    result.results.reduce((s, q) => s + q.maximum_points, 0) === 0
      ? 0
      : Math.round(
          (result.total_score /
            result.results.reduce((s, q) => s + q.maximum_points, 0)) *
            100,
        )

  return (
    <div className="lab-report">
      {/* Score header */}
      <div className="lab-report-score-header">
        <div className="lab-report-score-circle">
          <span className="lab-report-score-num">{result.total_score}</span>
          <span className="lab-report-score-max">
            / {result.results.reduce((s, q) => s + q.maximum_points, 0)}
          </span>
        </div>
        <div className="lab-report-score-pct">{totalPercent}%</div>
        <div className="lab-report-meta-badge">
          {result.meta.provider === 'gemini' ? '✦ Gemini' : '◆ OpenAI'} · {result.meta.model}
        </div>
      </div>

      {/* Overall feedback */}
      <div className="lab-report-section">
        <div className="lab-report-section-title">Genel Değerlendirme</div>
        <p className="lab-report-overall">{result.overall_feedback}</p>
      </div>

      {/* Per-question cards */}
      <div className="lab-report-section">
        <div className="lab-report-section-title">Sorular</div>
        <div className="lab-qcards">
          {result.results.map(q => {
            const isSelected = selected === q.question_number
            const color = scoreColor(q.score, q.maximum_points)
            const borderCls = {
              green:  'lab-qcard--green',
              orange: 'lab-qcard--orange',
              red:    'lab-qcard--red',
            }[color]
            return (
              <div
                key={q.question_number}
                ref={el => { cardRefs.current[q.question_number] = el }}
                className={`lab-qcard ${borderCls} ${isSelected ? 'lab-qcard--selected' : ''}`}
                onClick={() => onSelect(q)}
              >
                <div className="lab-qcard-header">
                  <span className="lab-qcard-qnum">S{q.question_number}</span>
                  <ScoreBadge score={q.score} max={q.maximum_points} size="sm" />
                  {showPageBadge && (
                    <span className="lab-qcard-page">sayfa {q.page_index + 1}</span>
                  )}
                </div>
                <p className="lab-qcard-feedback">{q.feedback}</p>
                {isSelected && q.student_answer && (
                  <div className="lab-qcard-answer">
                    <span className="lab-qcard-answer-label">Öğrenci cevabı:</span>
                    <span>{q.student_answer}</span>
                  </div>
                )}
                {isSelected && (
                  <div className="lab-qcard-reasoning">
                    <Info size={11} />
                    <span>{q.reasoning}</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Main Lab page ────────────────────────────────────────────────────────────

export default function LabPage() {
  // Upload state
  const [pageFiles, setPageFiles] = useState<File[]>([])
  const [rubricFile, setRubricFile] = useState<File[]>([])
  const [questionsFile, setQuestionsFile] = useState<File[]>([])
  const [rubricText, setRubricText] = useState('')
  const [provider, setProvider] = useState<'openai' | 'gemini'>('openai')
  const [feedbackLanguage, setFeedbackLanguage] = useState('Turkish')
  const [usePageLocator, setUsePageLocator] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Results
  const [result, setResult] = useState<LabResult | null>(null)
  const [activeTab, setActiveTab] = useState<'level1' | 'level2'>('level1')
  const [selected, setSelected] = useState<string | null>(null)

  const handleGrade = async () => {
    if (pageFiles.length === 0) {
      setError('En az bir öğrenci sayfası yükleyin.')
      return
    }
    setError(null)
    setLoading(true)
    setResult(null)
    setSelected(null)

    try {
      const fd = new FormData()
      pageFiles.forEach(f => fd.append('pages[]', f))
      if (rubricFile[0]) fd.append('rubric', rubricFile[0])
      if (questionsFile[0]) fd.append('questions', questionsFile[0])
      if (rubricText.trim()) fd.append('rubric_text', rubricText.trim())
      fd.append('provider', provider)
      fd.append('feedback_language', feedbackLanguage)
      if (provider === 'openai' && usePageLocator) {
        fd.append('use_page_locator', '1')
      }

      const resp = await fetch('/lab/grade', { method: 'POST', body: fd })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`)
      setResult(data as LabResult)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata')
    } finally {
      setLoading(false)
    }
  }

  const canGrade = pageFiles.length > 0 && !loading

  return (
    <div className="lab-page">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="lab-header">
        <div className="lab-header-left">
          <FlaskConical size={22} className="lab-header-icon" />
          <div>
            <h1 className="lab-header-title">Grading Lab</h1>
            <p className="lab-header-sub">
              Seviye 1 (yan yana) ve Seviye 2 (etiket pinleri) görselleştirmesini karşılaştırın
            </p>
          </div>
        </div>

        {/* Provider toggle */}
        <div className="lab-provider-toggle">
          <button
            className={`lab-provider-btn ${provider === 'openai' ? 'lab-provider-btn--active' : ''}`}
            onClick={() => setProvider('openai')}
            type="button"
          >
            ◆ OpenAI
          </button>
          <button
            className={`lab-provider-btn ${provider === 'gemini' ? 'lab-provider-btn--active' : ''}`}
            onClick={() => setProvider('gemini')}
            type="button"
          >
            ✦ Gemini
          </button>
        </div>
      </div>

      {/* ── Upload form ─────────────────────────────────────────────────── */}
      <div className="lab-upload-panel">
        <div className="lab-upload-grid">
          <DropZone
            label="Öğrenci Sayfaları *"
            hint="1 veya daha fazla sayfa — sırayla yükleyin"
            accept=".jpg,.jpeg,.png,.webp"
            multiple
            files={pageFiles}
            onFiles={setPageFiles}
          />

          <div className="lab-upload-col">
            <DropZone
              label="Rubrik / Cevap Anahtarı"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              files={rubricFile}
              onFiles={setRubricFile}
            />
            <div className="lab-rubric-text-wrap">
              <label className="lab-field-label">veya Rubrik Metni</label>
              <textarea
                className="lab-rubric-textarea"
                placeholder="Rubrik içeriğini buraya yapıştırın…"
                rows={4}
                value={rubricText}
                onChange={e => setRubricText(e.target.value)}
              />
            </div>
          </div>

          <div className="lab-upload-col">
            <DropZone
              label="Sorular PDF (opsiyonel)"
              hint="Yoksa model sorularını kağıttan çıkarır"
              accept=".pdf"
              files={questionsFile}
              onFiles={setQuestionsFile}
            />

            <div className="lab-settings-row">
              <label className="lab-field-label">Geri Bildirim Dili</label>
              <select
                className="lab-select"
                value={feedbackLanguage}
                onChange={e => setFeedbackLanguage(e.target.value)}
              >
                <option value="Turkish">Türkçe</option>
                <option value="English">İngilizce</option>
              </select>
            </div>

            <label className={`lab-locator-toggle ${provider !== 'openai' ? 'lab-locator-toggle--disabled' : ''}`}>
              <input
                type="checkbox"
                checked={usePageLocator}
                disabled={provider !== 'openai'}
                onChange={e => setUsePageLocator(e.target.checked)}
              />
              <span>
                <strong>Yüksek doğruluk</strong>
                <small>Her sayfa için ek locator çağrısı yapar; etiket pinlerini daha iyi konumlandırır.</small>
              </span>
            </label>
          </div>
        </div>

        {error && (
          <div className="lab-error">
            <AlertCircle size={15} />
            <span>{error}</span>
          </div>
        )}

        <div className="lab-grade-row">
          <button
            className="lab-grade-btn"
            onClick={handleGrade}
            disabled={!canGrade}
            type="button"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="lab-spin" />
                Değerlendiriliyor…
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Değerlendir
                <ChevronRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      {result && (
        <div className="lab-results">
          {/* Success bar */}
          <div className="lab-result-bar">
            <CheckCircle2 size={16} className="lab-result-bar-icon" />
            <span>
              Değerlendirme tamamlandı — toplam {result.total_score} puan ·{' '}
              {result.results.length} soru ·{' '}
              {result.page_file_ids.length} sayfa
              {result.meta.page_locator_enabled ? ' · yüksek doğruluk açık' : ''}
            </span>
          </div>

          {/* Tab strip */}
          <div className="lab-tabs">
            <button
              className={`lab-tab ${activeTab === 'level1' ? 'lab-tab--active' : ''}`}
              onClick={() => setActiveTab('level1')}
              type="button"
            >
              <span className="lab-tab-num">1</span>
              Seviye 1 — Yan Yana
            </button>
            <button
              className={`lab-tab ${activeTab === 'level2' ? 'lab-tab--active' : ''}`}
              onClick={() => setActiveTab('level2')}
              type="button"
            >
              <span className="lab-tab-num">2</span>
              Seviye 2 — Etiket Pinleri
            </button>
          </div>

          {/* Tab content */}
          <div className="lab-tab-content">
            {activeTab === 'level1' ? (
              <Level1Tab result={result} selected={selected} onSelect={setSelected} />
            ) : (
              <Level2Tab result={result} selected={selected} onSelect={setSelected} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
